import { useCallback, useEffect, useMemo, useState } from "react";
import { useDeposits } from "./useDeposits.js";
import { fetchTransactionsByOwner } from "../services/api.js";
import { chainNowSec } from "../utils/chainClock.js";

/**
 * Account-scoped notification feed for the header bell.
 *
 * Per client (Jun 2026): the bell must surface ONLY things about the
 * connected wallet's own positions — never protocol-health / system alerts
 * (those would "unnecessarily worry people"). System alerts still drive the
 * PausedDepositsBanner; they just no longer pop the bell.
 *
 * Notifications are DERIVED client-side from the user's deposits +
 * transactions (there is no per-account alert collection on the backend):
 *   - Auto-Reinvest created a new Term Deposit   (compound_executed tx)
 *   - Term Deposit passed its 1-year / 2-year mark   (startTime + interval)
 *   - Term Deposit has matured                   (now ≥ maturityTime)
 *
 * Because these are derived (not backend rows), "Dismiss" is a LOCAL
 * action — dismissed ids persist in localStorage so a notification stays
 * gone across reloads without a backend write.
 */
const DISMISS_KEY = "gilder.dismissedNotifications";
const ONE_YEAR_SEC = 365 * 24 * 60 * 60;

function loadDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return new Set();
    }
}

function saveDismissed(set) {
    try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
    } catch {
        /* storage unavailable (private mode) — dismissal just won't persist */
    }
}

export function useAccountNotifications(address, { pollMs = 60_000 } = {}) {
    const { deposits } = useDeposits(address);
    const [compoundTxns, setCompoundTxns] = useState([]);
    const [dismissed, setDismissed] = useState(() => loadDismissed());
    const [isLoading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!address) {
            setCompoundTxns([]);
            setLoading(false);
            return;
        }
        try {
            const data = await fetchTransactionsByOwner(address, {
                type: "compound_executed",
                limit: 25
            });
            setCompoundTxns(data?.items || []);
        } catch {
            // Best-effort — milestones (from deposits) still render.
        } finally {
            setLoading(false);
        }
    }, [address]);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const id = setInterval(refresh, pollMs);
        return () => clearInterval(id);
    }, [refresh, pollMs]);

    const items = useMemo(() => {
        if (!address) return [];
        const now = chainNowSec();
        const out = [];

        // Auto-Reinvest minted a fresh TD.
        for (const tx of compoundTxns) {
            const id = `compound-${tx.txHash}-${tx.logIndex}`;
            const tdLabel = tx.depositId != null ? ` (TD #${String(tx.depositId).padStart(6, "0")})` : "";
            out.push({
                _id: id,
                type: "auto_reinvest_new_td",
                message: `Auto-Reinvest created a new Term Deposit${tdLabel}`,
                createdAt: tx.timestamp,
                severity: "info"
            });
        }

        // Per-deposit time milestones, derived from the deposit's own clock.
        for (const d of deposits) {
            const state = Number(d.state);
            // Skip fully-closed positions (exited/closed) — only live or
            // matured deposits are worth notifying about.
            if (state === 5 || state === 6) continue;
            const tdLabel = `TD #${String(d.id).padStart(6, "0")}`;
            const start = Number(d.startTime) || 0;
            const maturity = Number(d.maturityTime) || 0;

            if (maturity > 0 && now >= maturity) {
                out.push({
                    _id: `matured-${d.id}`,
                    type: "td_matured",
                    message: `${tdLabel} has matured — withdraw or roll it over`,
                    createdAt: new Date(maturity * 1000).toISOString(),
                    severity: "info"
                });
            } else if (start > 0) {
                // Only the highest milestone reached is shown (a 2-year-old
                // deposit shows "2-year", not both).
                const twoYear = start + 2 * ONE_YEAR_SEC;
                const oneYear = start + ONE_YEAR_SEC;
                if (now >= twoYear) {
                    out.push({
                        _id: `milestone-${d.id}-2y`,
                        type: "td_two_year",
                        message: `${tdLabel} passed its 2-year mark`,
                        createdAt: new Date(twoYear * 1000).toISOString(),
                        severity: "info"
                    });
                } else if (now >= oneYear) {
                    out.push({
                        _id: `milestone-${d.id}-1y`,
                        type: "td_one_year",
                        message: `${tdLabel} passed its 1-year mark`,
                        createdAt: new Date(oneYear * 1000).toISOString(),
                        severity: "info"
                    });
                }
            }
        }

        return out
            .filter((n) => !dismissed.has(n._id))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }, [address, compoundTxns, deposits, dismissed]);

    const dismiss = useCallback((id) => {
        setDismissed((prev) => {
            const next = new Set(prev);
            next.add(id);
            saveDismissed(next);
            return next;
        });
    }, []);

    return { items, unreadCount: items.length, isLoading, refresh, dismiss };
}
